import type { LegalBundle } from "./types";

const en: LegalBundle = {
  draftNote:
    "DRAFT — pending professional legal review (including MiCA applicability). This text is provided for review and does not yet constitute final legal terms.",
  nav: {
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
    marketplace: "Marketplace Terms",
    guidelines: "Community Guidelines",
    "content-ip": "Content & IP",
    risk: "Risk Disclosure",
    support: "Support",
    moderation: "Moderation & Reporting",
  },
  docs: {
    terms: {
      eyebrow: "Legal",
      title: "Terms of Service",
      intro:
        "These Terms govern your access to and use of THE GRID. By connecting a wallet and using the product you agree to them.",
      sections: [
        { heading: "1. The service", body: ["THE GRID is a digital spatial platform where users acquire, hold and present on-chain territory (parcels) and related content. Core state is authoritative on-chain; the application presents and indexes that state."] },
        { heading: "2. Eligibility", body: ["You must be of legal age in your jurisdiction (at least 18) and legally able to use blockchain wallets and digital assets. You are responsible for compliance with your local law."] },
        { heading: "3. Wallets & accounts", body: ["Access uses Sign-In With Ethereum (SIWE). You are solely responsible for the security of your wallet and keys. We never take custody of your keys and cannot recover them."] },
        { heading: "4. Acceptable use", body: ["You agree not to misuse the service, attempt to bypass on-chain authority, interfere with indexing, or upload unlawful or infringing content. See the Community Guidelines and Content & IP policy."] },
        { heading: "5. No financial advice", body: ["Nothing here is investment, financial, tax or legal advice. Digital assets carry risk — see the Wallet / Blockchain Risk Disclosure."] },
        { heading: "6. Changes", body: ["These Terms may be updated. Material changes will be surfaced in-product. Continued use after changes constitutes acceptance."] },
      ],
    },
    privacy: {
      eyebrow: "Legal",
      title: "Privacy Policy",
      intro:
        "This policy explains what data THE GRID processes and why. We minimise data and use NO advertising or profiling trackers.",
      sections: [
        { heading: "Data we process", body: ["Public wallet address (to identify your session and on-chain holdings).", "A session identifier delivered via an HttpOnly cookie — the server stores ONLY a SHA-256 hash of the session token, never the token itself.", "On-chain public data (parcels, purchases, transfers) read from the blockchain and public RPC.", "User-generated content you choose to publish (e.g. territory canvases)."] },
        { heading: "What we do NOT do", body: ["No advertising trackers. No profiling trackers. No marketing pixels. No sale of personal data."] },
        { heading: "Blockchain immutability", body: ["Data written to the blockchain (e.g. ownership, purchases) is public and permanent. It cannot be edited or deleted by us or by you. Consider this before transacting."] },
        { heading: "Retention", body: ["Session records expire automatically (TTL). Indexed on-chain data mirrors the chain and is retained while the service operates. UGC is retained until withdrawn or removed per policy."] },
        { heading: "Your choices", body: ["You can disconnect your wallet and end your session at any time. On-chain data remains on-chain by its nature."] },
        { heading: "Third parties", body: ["We rely on public RPC/blockchain infrastructure to read chain state. We do not share personal data with advertisers."] },
      ],
    },
    cookies: {
      eyebrow: "Legal",
      title: "Cookie Policy",
      intro:
        "THE GRID uses only strictly-necessary cookies. We do not use analytics, advertising or profiling cookies.",
      sections: [
        { heading: "Strictly necessary only", body: ["A single HttpOnly session cookie is used to keep you signed in after SIWE authentication. It is essential to the service and carries no tracking function."] },
        { heading: "Why no consent banner", body: ["Under common cookie rules, strictly-necessary cookies do not require prior consent. Because we use no analytics, advertising or profiling cookies, no cookie-consent banner is presented. This technical basis is documented here and is subject to professional legal review, which may require a different treatment in some jurisdictions."] },
        { heading: "No tracking technologies", body: ["No marketing pixels, no fingerprinting, no cross-site trackers, no third-party advertising cookies are used."] },
      ],
    },
    marketplace: {
      eyebrow: "Legal",
      title: "Marketplace Terms",
      intro:
        "These terms apply when you list, buy or sell land or artworks on THE GRID marketplace.",
      sections: [
        { heading: "Peer-to-peer", body: ["Marketplace transactions are peer-to-peer and settled by on-chain smart contracts. The platform is not a counterparty to your trade."] },
        { heading: "On-chain authority", body: ["Ownership, transfers and settlement are governed by the smart contracts. The application indexes and displays this state; the chain is authoritative."] },
        { heading: "Fees", body: ["Any protocol/marketplace fees are defined on-chain and disclosed at the point of transaction. Network gas fees are set by the blockchain, not by us."] },
        { heading: "Risk & finality", body: ["Blockchain transactions are generally irreversible. Verify details before signing. See the Risk Disclosure."] },
        { heading: "Disputes", body: ["Because trades settle on-chain peer-to-peer, we have limited ability to reverse them. Report policy violations via Moderation & Reporting."] },
      ],
    },
    guidelines: {
      eyebrow: "Community",
      title: "Community Guidelines",
      intro: "THE GRID is a shared canvas. These guidelines keep it safe and usable for everyone.",
      sections: [
        { heading: "Be lawful", body: ["No illegal content, hate speech, harassment, sexual content involving minors, or incitement to violence."] },
        { heading: "Respect rights", body: ["Only publish content you have the right to use. Do not infringe trademarks, copyrights or privacy."] },
        { heading: "No abuse of the platform", body: ["Do not attempt to manipulate indexing, impersonate others, or deploy malicious links in published content."] },
        { heading: "Enforcement", body: ["Violations may lead to content removal or restrictions, per the Moderation & Reporting process."] },
      ],
    },
    "content-ip": {
      eyebrow: "Legal",
      title: "Content & IP Policy",
      intro: "You keep ownership of what you create; you are responsible for having the rights to publish it.",
      sections: [
        { heading: "Your content", body: ["You retain ownership of content you create and publish on your territory. You grant the platform a limited licence to host and display it as part of the service."] },
        { heading: "Rights you must have", body: ["You represent that you own or are licensed to use all content you publish, including images, text and artwork."] },
        { heading: "Infringement", body: ["We respond to valid intellectual-property complaints and may remove infringing content. Submit reports via Moderation & Reporting."] },
      ],
    },
    risk: {
      eyebrow: "Risk",
      title: "Wallet & Blockchain Risk Disclosure",
      intro: "Using blockchain wallets and digital assets involves significant risk. Read this before transacting.",
      sections: [
        { heading: "Self-custody", body: ["You control your wallet and keys. If you lose your keys or seed phrase, no one — including us — can recover your assets."] },
        { heading: "Irreversibility", body: ["Blockchain transactions are generally final and cannot be reversed. Always verify recipient, amount and network before signing."] },
        { heading: "Network & smart-contract risk", body: ["Smart contracts and networks can have bugs, congestion or outages. Gas fees and confirmation times vary."] },
        { heading: "No advice / volatility", body: ["Digital assets can be volatile. Nothing in THE GRID is investment advice. Only transact what you can afford to lose."] },
        { heading: "Network identity", body: ["Verify you are on the intended network. Test networks use valueless tokens; main networks use real-value assets."] },
        { heading: "Age", body: ["Wallet and economic functionality is intended for users who are at least 18 years old."] },
      ],
    },
    support: {
      eyebrow: "Support",
      title: "Support & Complaints",
      intro: "Need help or want to raise a complaint? Here is how to reach us.",
      sections: [
        { heading: "Getting help", body: ["For product questions, use the in-product help surfaces. For account/session issues, disconnect and reconnect your wallet first."] },
        { heading: "Complaints", body: ["Complaints are handled on a best-effort basis. Provide your wallet address, the relevant transaction hash (if any) and a clear description."] },
        { heading: "What we cannot do", body: ["We cannot reverse on-chain transactions, recover lost keys, or move your assets. These are inherent properties of self-custody."] },
      ],
    },
    moderation: {
      eyebrow: "Trust & Safety",
      title: "Moderation & Reporting",
      intro: "How to report content and how moderation works on THE GRID.",
      sections: [
        { heading: "Reporting", body: ["Report content that violates the Community Guidelines or infringes rights. Include the territory/parcel reference and the reason."] },
        { heading: "Review", body: ["Reports are reviewed and, where warranted, content is removed or restricted. We aim to act proportionately and consistently."] },
        { heading: "Appeals", body: ["If your content was actioned and you believe this was an error, you may contact support with context."] },
      ],
    },
  },
};

export default en;
